import {
  IFeatureFlagClient,
  IFlagSet,
  UserSegmentationObject,
  FeatureFlagOptions,
  FeatureFlagClient,
} from "./featureFlagClient";

export interface IFeatureFlagClientManager {
  /**
   * Creates a brand new client and caches it.
   * @param clientId
   * @param user
   * @param options
   */
  getClient(
    clientId?: string,
    user?: UserSegmentationObject,
    options?: FeatureFlagOptions,
    targetFlags?: IFlagSet
  ): IFeatureFlagClient;
  /**
   * Return the current service or undefined.
   */
  getInstance(): this;

  readonly _clients: Map<string, IFeatureFlagClient>;
}
let _instance: IFeatureFlagClientManager = undefined;
const _clients: Map<string, IFeatureFlagClient> = new Map();
const _users: Map<string, UserSegmentationObject> = new Map();
const _options: Map<string, FeatureFlagOptions> = new Map();
const _targetFlags: Map<string, IFlagSet> = new Map();

const getInstance = () => {
  if (!_instance) {
    _instance = {
      getClient,
      getInstance,
      _clients,
    };
  }

  return _instance;
};

const getClient = (
  clientId: string = window["featureFlagParams"]?.["clientId"],
  user: UserSegmentationObject = window["featureFlagParams"]?.["user"],
  options: FeatureFlagOptions = window["featureFlagParams"]?.["options"],
  targetFlags?: IFlagSet
) => {
  if (
    !_clients.has(clientId) ||
    options !== _options.get(clientId) ||
    targetFlags !== _targetFlags.get(clientId)
  ) {
    // Client hasn't been initialized before OR the options and flags are new.
    // Remove the client ids from the caches and then create with the new ones.
    // Delete will return false if the element does not exist and will not throw error.
    _clients.delete(clientId);
    _users.delete(clientId);
    _options.delete(clientId);
    _targetFlags.delete(clientId);

    createClientAndCache(clientId, user, options, targetFlags);
  } else if (_users.get(clientId) !== user) {
    // User has changed but nothing else, remove the old one, change the user and then add that new user back.
    _users.delete(clientId);

    _clients.get(clientId).changeUser(user);
    _users.set(clientId, user);
  }

  return _clients.get(clientId);
};

const createClientAndCache = (
  clientId: string = window["featureFlagParams"]?.["clientId"],
  user: UserSegmentationObject = window["featureFlagParams"]?.["user"],
  options: FeatureFlagOptions = window["featureFlagParams"]?.["options"],
  targetFlags: IFlagSet
) => {
  const client = FeatureFlagClient(clientId, user, options, targetFlags);
  _clients.set(clientId, client);
  _users.set(clientId, user);
  _options.set(clientId, options);
  _targetFlags.set(clientId, targetFlags);
};

export const FeatureFlagClientManager: IFeatureFlagClientManager = {
  getClient,
  getInstance,
  _clients
};